import { extractRoomIdFromPath } from "../../domains/room/utils/roomIdFromPath";
import { useApiQuery } from "../../services/api";
import { Drawer } from "@mui/material";
import { useLocation } from "react-router-dom";
import {
  LOGIN_SIGNUP_LANGUAGES,
  PUBLIC_INAPP_LANGUAGES,
} from "../../config/i18n";
import {
  useCurrentUser,
  reloadCurrentUser,
} from "../../domains/user/hooks/useCurrentUser";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import { apiClient } from "../../services/api";
import { HapticEffect, SupportedLanguage } from "../../interfaces";
import {
  parseSupportedLanguage,
  storePublicLang,
} from "../../domains/app/utils/langUtils";
import { setRoomContentLang } from "../../domains/app/stores/roomContentLangStore";
import { navClickChangeLanguage } from "../../tools/analytics/domains/nav.analytics";
import { iosGenerateHaptic } from "../../tools/ios";
import { IconCheck, IconX } from "@tabler/icons-react";
import { cn } from "../../lib/utils";
import { useTranslation } from "react-i18next";

const LANGUAGE_OPTIONS: Record<SupportedLanguage, string> = {
  en: "English",
  fr: "Français",
  de: "Deutsch",
  es: "Español",
  it: "Italiano",
  pt: "Português",
  ar: "اللغة العربية",
  nl: "Nederlands",
};

type Props = {
  isVisible: boolean;
  onClose: () => void;
  allowedLanguages?: string[];
};

export default function LanguagePanel(props: Props) {
  const { isVisible, onClose, allowedLanguages: allowedLanguagesProp } = props;
  const location = useLocation();
  const { currentUser } = useCurrentUser();
  const _roomId = extractRoomIdFromPath(useLocation().pathname);
  const _roomQuery = useApiQuery(
    "/webapp/private/rooms/:id",
    _roomId !== null ? { param: { id: _roomId } } : false
  );
  const currentRoom = _roomQuery.state === "success" ? _roomQuery.body : null;
  const { activeLang, contentLang, isRtl, changeActiveLang } = useActiveLang();
  const { t } = useTranslation();
  const isOnLoginSignup = ["/login", "/signup"].some((pathRoot: string) =>
    location.pathname.startsWith(pathRoot)
  );
  const availableLanguages = allowedLanguagesProp
    ? allowedLanguagesProp
    : currentRoom
      ? currentRoom.allowedLanguages
      : isOnLoginSignup
        ? LOGIN_SIGNUP_LANGUAGES
        : PUBLIC_INAPP_LANGUAGES;

  function handleChange(lang: SupportedLanguage) {
    iosGenerateHaptic(HapticEffect.SELECTION);

    if (currentRoom !== null) {
      // In-room picker sets the CONTENT language only; the app UI always
      // follows user.language and must not change here.
      const initialLanguage = contentLang;
      setRoomContentLang({ language: lang, source: "explicit" });
      navClickChangeLanguage(lang, {
        initial_language: initialLanguage,
        origin: "room",
      });
      if (currentUser !== null) {
        void apiClient
          .patch("/webapp/private/users/room/:roomId/settings", {
            param: { roomId: currentRoom.id },
            json: { language: lang },
          })
          .then(() => {
            reloadCurrentUser();
          });
      }
    } else {
      const initialLanguage = activeLang;
      changeActiveLang(lang);
      navClickChangeLanguage(lang, {
        initial_language: initialLanguage,
        origin: "public app",
      });
      // Public app picker: updates user.language (not just a session toggle).
      // Switching en↔fr here changes the user's main app language, which
      // propagates to app pages (settings, profile, GPT) and room resolution.
      storePublicLang(lang);
      if (currentUser !== null) {
        void apiClient
          .patch("/webapp/private/users/me", {
            json: { language: lang },
          })
          .then(() => {
            reloadCurrentUser();
          });
      }
    }
  }

  return (
    <Drawer
      anchor={isRtl ? "left" : "right"}
      open={isVisible}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: "100%",
          padding: "64px 36px",
          display: "flex",
          flexDirection: "column",
          gap: "24px",
          background: "var(--color-ds-white)",
          "@media (min-width: 768px)": {
            left: "50% !important",
            right: "auto !important",
            transform: "translateX(-50%) !important",
            maxWidth: "600px",
            width: "600px",
          },
        },
        "& .MuiBackdrop-root": {
          "@media (min-width: 768px)": {
            left: "50% !important",
            right: "auto !important",
            transform: "translateX(-50%) !important",
            maxWidth: "600px",
            width: "600px",
          },
        },
      }}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1 text-center text-base/tight font-semibold  text-primary-black">
          {t("Choose language")}
        </div>
        <button
          type="button"
          aria-label={t("Close")}
          onClick={onClose}
          className="flex size-10 items-center justify-center rounded-full bg-primary-black text-white cursor-pointer transition-opacity hover:opacity-80"
        >
          <IconX className="size-5" />
        </button>
      </div>

      <hr className="border-t border-primary-black/15" />

      <div className="flex flex-col gap-6">
        {/* NOTE: Can't bring these texts from i18n, as all languages may not be loaded yet */}
        {Object.entries(LANGUAGE_OPTIONS)
          .filter(([lang]) => availableLanguages.includes(lang))
          .map(([lang, text]) => {
            const parsed = parseSupportedLanguage(lang);
            if (parsed === undefined) return null;
            // In a room the selector reflects the content language; elsewhere
            // it reflects the UI language (which equals user.language).
            const checked =
              currentRoom !== null
                ? parsed === contentLang
                : parsed === activeLang;
            return (
              <button
                key={lang}
                type="button"
                onClick={() => {
                  handleChange(parsed);
                }}
                className="flex items-center gap-4 cursor-pointer text-left"
              >
                <span
                  className={cn(
                    "flex size-7 shrink-0 items-center justify-center rounded-full border-2 transition-colors duration-200 ease-out",
                    checked
                      ? "border-ds-btn-primary bg-ds-btn-primary"
                      : "border-primary-black bg-transparent"
                  )}
                >
                  <IconCheck
                    className={cn(
                      "size-4 text-ds-on-primary transition-[transform,opacity] duration-200 ease-out",
                      checked ? "scale-100 opacity-100" : "scale-50 opacity-0"
                    )}
                    stroke={3}
                  />
                </span>
                <span className="text-lg font-bold text-primary-black">
                  {text}
                </span>
              </button>
            );
          })}
      </div>
    </Drawer>
  );
}
